import React, { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Alert, Button } from 'antd';
import { DownloadOutlined, PlayCircleOutlined } from '@ant-design/icons';
import { useGetStudyDetailQuery, type DicomRow } from 'services/exploreService';
import {
  DicomCard,
  Empty,
  Kpi,
  PageHead,
  Panel,
  Skeleton,
  fmt,
  viewerHref,
} from 'components/explore';
import DicomInspector from 'components/explore/DicomInspector';

type Grouping = 'view' | 'order';

const UNCLASSIFIED = 'No view';

const groupKey = (d: DicomRow) => d.labeled_view || d.predicted_view || UNCLASSIFIED;

const MEASURES: { key: string; label: string; unit: string }[] = [
  { key: 'ejection_fraction', label: 'EF', unit: '%' },
  { key: 'left_ventricle_diastolic_diameter', label: 'LVIDd', unit: 'cm' },
  { key: 'left_ventricle_systolic_diameter', label: 'LVIDs', unit: 'cm' },
  { key: 'left_ventricle_septal_wall_thickness', label: 'IVSd', unit: 'cm' },
  { key: 'left_ventricle_posterior_wall_thickness', label: 'LVPWd', unit: 'cm' },
  { key: 'left_atrium_dimensions', label: 'LA', unit: 'cm' },
  { key: 'aortic_root_diameter', label: 'Ao root', unit: 'cm' },
  { key: 'right_ventricle_diameter', label: 'RVIDd', unit: 'cm' },
  { key: 'mitral_valve_ea_ratio', label: 'MV E/A', unit: '' },
  { key: 'pulmonary_artery_pressure', label: 'PASP', unit: 'mmHg' },
];

const downloadStudyZip = (studyUuid: string) => {
  const token = localStorage.getItem('firebase_id_token');
  const base = (process.env.REACT_APP_API_MAIN_URL || '').replace(/\/$/, '');
  const a = document.createElement('a');
  a.href = `${base}/api/v1/dicoms/study/${studyUuid}/download-zip?token=${token}`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
};

const StudyDetail: React.FC = () => {
  const { key = '' } = useParams();
  const { data, isLoading, error } = useGetStudyDetailQuery(key, { skip: !key });
  const [grouping, setGrouping] = useState<Grouping>('view');
  const [open, setOpen] = useState<number | null>(null);

  const groups = useMemo(() => {
    const dicoms = data?.dicoms ?? [];
    if (grouping === 'order') return [{ name: 'All DICOMs', items: dicoms }];
    const map = new Map<string, DicomRow[]>();
    dicoms.forEach(d => {
      const k = groupKey(d);
      map.set(k, [...(map.get(k) ?? []), d]);
    });
    return Array.from(map.entries())
      .sort(([a, x], [b, y]) =>
        a === UNCLASSIFIED ? 1 : b === UNCLASSIFIED ? -1 : y.length - x.length
      )
      .map(([name, items]) => ({ name, items }));
  }, [data, grouping]);

  // Inspector steps through DICOMs in the order they are shown.
  const ordered = useMemo(() => groups.flatMap(g => g.items), [groups]);
  const indexOf = useMemo(() => new Map(ordered.map((d, i) => [d.uuid, i])), [ordered]);

  if (error) {
    const notFound = (error as any)?.status === 404;
    return (
      <div className="ax-page">
        <PageHead eyebrow={<Link to="/studies">← Studies</Link>} title={key} />
        <Alert
          type={notFound ? 'warning' : 'error'}
          showIcon
          message={notFound ? 'Study not found' : 'Could not load this study'}
          description={
            notFound ? 'No study matches this ICID or UUID.' : 'The Explorer API returned an error.'
          }
        />
      </div>
    );
  }

  if (isLoading || !data) {
    return (
      <div className="ax-page">
        <Skeleton h={34} w="40%" />
        <div className="ax-tiles">
          {Array.from({ length: 4 }).map((_, i) => (
            <div className="ax-tile" key={i}>
              <Skeleton h={28} w="50%" />
            </div>
          ))}
        </div>
        <Skeleton h={240} />
      </div>
    );
  }

  const { study, report, dicoms } = data;
  const conclusions = report?.sections.find(x => x.key === 'conclusions')?.text;
  const n = dicoms.length;
  const count = (f: (d: DicomRow) => boolean) => dicoms.filter(f).length;
  const disagreements = count(
    d => !!d.labeled_view && !!d.predicted_view && d.labeled_view !== d.predicted_view
  );

  return (
    <div className="ax-page">
      <PageHead
        eyebrow={
          <>
            <Link to="/studies" className="ax-link" style={{ fontFamily: 'inherit' }}>
              ← Studies
            </Link>
            {'  ·  '}
            {[study.datasource, study.visit_year].filter(Boolean).join(' · ')}
          </>
        }
        title={
          <span className="ax-mono" style={{ fontFamily: 'var(--f-display)' }}>
            {study.icid ?? study.uuid}
          </span>
        }
        lede={
          [
            study.age_at_visit && `${study.age_at_visit} years`,
            study.gender,
            study.race,
            study.designation,
            study.foreign_id && `foreign id ${study.foreign_id}`,
          ]
            .filter(Boolean)
            .join(' · ') || undefined
        }
        actions={
          <>
            {n > 0 && (
              <Link to={viewerHref(dicoms[0].icid)}>
                <Button type="primary" icon={<PlayCircleOutlined />}>
                  Open in viewer
                </Button>
              </Link>
            )}
            <Button icon={<DownloadOutlined />} onClick={() => downloadStudyZip(study.uuid)}>
              Download DICOMs
            </Button>
          </>
        }
      />

      <section className="ax-tiles" aria-label="Study summary">
        <Kpi
          value={fmt(n)}
          label="DICOMs"
          sub={
            study.number_of_usable_dicoms != null
              ? `${fmt(study.number_of_usable_dicoms)} usable`
              : undefined
          }
          color="var(--s1)"
        />
        <Kpi value={fmt(count(d => !!d.predicted_view))} label="With AI view" color="var(--s1)" />
        <Kpi
          value={fmt(count(d => !!d.labeled_view))}
          label="With tech view label"
          sub={disagreements ? `${disagreements} disagree with AI` : undefined}
          color="var(--s2)"
        />
        <Kpi value={fmt(count(d => d.has_segmentation))} label="Segmented" color="var(--s3)" />
        <Kpi value={fmt(count(d => d.has_keypoints))} label="With keypoints" color="var(--s4)" />
      </section>

      <Panel
        eyebrow="Report"
        title={report ? `Signed by ${report.signer ?? 'unknown'}` : 'No structured report'}
        sub={report?.date_created ? new Date(report.date_created).toLocaleDateString() : undefined}
      >
        {report ? (
          <>
            <div className="ax-measures">
              {MEASURES.map(m => ({ m, v: report.measurements.find(x => x.key === m.key) }))
                .filter(({ v }) => v && v.value !== null && String(v.value).trim() !== '')
                .map(({ m, v }) => (
                  <div className="ax-measure" key={m.key}>
                    <div className="k">{m.label}</div>
                    <div className="v">
                      {String(v!.value)}
                      {m.unit && <small>{v!.unit || m.unit}</small>}
                    </div>
                  </div>
                ))}
            </div>
            {conclusions && (
              <p className="ax-note" style={{ whiteSpace: 'pre-wrap', maxWidth: '90ch' }}>
                {conclusions}
              </p>
            )}
            {report.findings.some(f => f.region) && (
              <div className="chips" style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {report.findings
                  .filter(f => f.region)
                  .map((f, i) => (
                    <span key={i} className="ax-pill" title={f.text ?? undefined}>
                      {f.region} · {(f.condition ?? '').replace(/_/g, ' ')}
                      {f.stratification ? ` · ${f.stratification}` : ''}
                    </span>
                  ))}
              </div>
            )}
            <span className="ax-faint" style={{ fontSize: 12 }}>
              All {report.measurements.length} measurements, findings and the report text are on
              each DICOM page.
            </span>
          </>
        ) : (
          <p className="ax-faint" style={{ margin: 0 }}>
            This study has no report in the warehouse.
          </p>
        )}
      </Panel>

      <Panel
        eyebrow="DICOMs"
        title={
          grouping === 'view'
            ? `${n} DICOMs in ${groups.length} views`
            : `${n} DICOMs in acquisition order`
        }
        sub="Grouped by tech label, or by AI prediction when unlabelled. Hover a tile to play the loop; click to inspect, then ← → to step through the study."
        actions={
          <div className="ax-seg" role="group" aria-label="Group DICOMs">
            <button
              type="button"
              aria-pressed={grouping === 'view'}
              onClick={() => setGrouping('view')}
            >
              By view
            </button>
            <button
              type="button"
              aria-pressed={grouping === 'order'}
              onClick={() => setGrouping('order')}
            >
              In order
            </button>
          </div>
        }
      >
        {n === 0 ? (
          <Empty title="No DICOMs in this study" />
        ) : (
          groups.map(g => (
            <div className="ax-viewgroup" key={g.name}>
              {grouping === 'view' && (
                <h3>
                  {g.name} <small>{g.items.length}</small>
                </h3>
              )}
              <div className="ax-gallery">
                {g.items.map(d => (
                  <DicomCard
                    key={d.uuid}
                    d={d}
                    onOpen={() => setOpen(indexOf.get(d.uuid) ?? null)}
                  />
                ))}
              </div>
            </div>
          ))
        )}
      </Panel>
      <DicomInspector items={ordered} index={open} onIndex={setOpen} />
    </div>
  );
};

export default StudyDetail;
